import { Card, Heading, Stack, Text, Center, Flex } from '@chakra-ui/react';
import { ReactNode } from 'react';

interface CardComponentProps {
  title: string;
  description: string;
  onButtonClick?: () => void;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  'data-e2e'?: string;
}

/**
 * @useFor a clickable summary tile with title + description + optional
 *   left / right icons — the entry-point card on a feature index
 *   page or a dashboard.
 *
 *   Examples in-tree: Settings landing page cards, Admin dashboard
 *   tiles, feature-discovery cards on empty states.
 *
 *   For metric/KPI cards (a single number + label) use `<StatCard>`.
 *   For grouping a section of form fields use `<SectionCard>`. This
 *   primitive is specifically for "click to navigate / open"
 *   summary tiles.
 */
export function CardComponent({
  title,
  description,
  onButtonClick,
  leftIcon,
  rightIcon,
  'data-e2e': dataE2e,
}: CardComponentProps) {
  return (
    <Card.Root
      w={'100%'}
      variant="outline"
      display="flex"
      flexDirection={{ base: 'column', sm: 'row' }}
      alignItems="center"
      borderRadius={'16px'}
      justifyContent={'space-between'}
      padding={'0px 8px'}
      onClick={onButtonClick}
      cursor={onButtonClick ? 'pointer' : 'default'}
      bg={"surface.primary"}
      data-e2e={dataE2e}
      borderWidth="1px"
      borderColor="border.line"
      _hover={
        onButtonClick
          ? {
            shadow: 'md',
            bg: 'surface.secondary',
          }
          : undefined
      }
    >
      <Flex>
        {leftIcon && <Center>{leftIcon}</Center>}

        <Stack>
          <Card.Body>
            <Heading size="md">{title}</Heading>
            <Text py="2" fontSize="xsm">
              {description}
            </Text>
          </Card.Body>
        </Stack>
      </Flex>

      {rightIcon && <Center>{rightIcon}</Center>}
    </Card.Root>
  );
}
